import React from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Divider from "@mui/material/Divider";
import ConfigurationBlockPreview from "./ConfigurationBlockPreview";
import SavedLogbookItemsDrawer from "./SavedLogbookItemsDrawer";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddIcon from "@mui/icons-material/Add";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import LogbookCategoryDrawer from "../LogbookCategoryDrawer";
import LogbookTemplateDrawer from "../LogbookTemplateDrawer";
import MetricBlockDrawer from "./MetricBlockDrawer";
import TableBlockDrawer from "./TableBlockDrawer";
import SectionBlockDrawer from "./SectionBlockDrawer";
import AgentBlockDrawer from "./AgentBlockDrawer";
import { LAYOUT_MENU_PROPS } from "./layoutMenuStyles";
import {
  canShareRow,
  insertBlock,
  moveRow,
  moveBlockWithinRow,
  moveBlockToNewRow,
} from "./layoutArrangement";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../../requests/utilities/requests";
import {
  API_get_logbook_layout,
  API_post_logbook_layout,
  API_get_logbook_metric_sources,
  API_get_logbook_columns,
  API_post_logbook_category,
  API_delete_logbook_category,
  API_get_logbook_templates,
  API_post_apply_logbook_template,
  API_get_logbook_agents,
  API_get_logbook_notify_users,
} from "../../../../constants";

const SPAN_BY_ROW_COUNT = {
  1: "col-span-12",
  2: "col-span-6 md:col-span-12",
  3: "col-span-4 md:col-span-12",
  4: "col-span-3 md:col-span-12",
};

const BLOCK_TYPES = [
  ["category", "Log category", "Collect notes, numbers, or ratings"],
  ["metric", "Metric card", "Show one key number"],
  ["table", "Metric table", "Compare several metrics"],
  ["agent", "AI agent", "Summarize and flag activity"],
  ["section", "Section header", "Group the items below a heading"],
];

const ACTION_CLASS =
  "inline-flex items-center justify-center rounded-lg p-1.5 text-slate-500 hover:bg-violet-50 hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-30 disabled:cursor-not-allowed";

const newUuid = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `blk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const DEFAULT_CATEGORY_MODAL_DATA = {
  name: "",
  type: "text",
  logbook_category_uuid: null,
  notify_users_enabled: false,
  notify_user_ids: [],
  description: "",
};

// The layout builder: rows of preview cards, a palette to add new ones,
// per-block settings drawers, and an explicit Save that persists the layout
// and projects category order back into the legacy columns (for mobile).
function LogbookLayoutBuilder(props) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [isLegacy, setIsLegacy] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [placement, setPlacement] = React.useState("end");
  const [addMenu, setAddMenu] = React.useState(null);
  const [savedItemsOpen, setSavedItemsOpen] = React.useState(false);
  const [moveMenu, setMoveMenu] = React.useState(null);

  const [columnsData, setColumnsData] = React.useState(null);
  const [metricSources, setMetricSources] = React.useState([]);
  // Company-scoped flag-notification recipients (NOT chat users: those span
  // every company the requesting admin belongs to).
  const [notifyUsers, setNotifyUsers] = React.useState([]);

  // Category details persist separately from their placement in the layout.
  const [categoryDrawerOpen, setCategoryDrawerOpen] = React.useState(false);
  const [categoryModalData, setCategoryModalData] = React.useState(
    DEFAULT_CATEGORY_MODAL_DATA
  );
  const [categoryModalEdit, setCategoryModalEdit] = React.useState(false);

  // Block settings drawers
  const [settingsBlock, setSettingsBlock] = React.useState(null);

  // Agents
  const [agents, setAgents] = React.useState([]);
  const [agentDrawerOpen, setAgentDrawerOpen] = React.useState(false);
  const [editingAgent, setEditingAgent] = React.useState(null);

  // Templates
  const [templateDrawerOpen, setTemplateDrawerOpen] = React.useState(false);
  const [categoryTemplates, setCategoryTemplates] = React.useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState(null);
  const [isApplyingTemplate, setIsApplyingTemplate] = React.useState(false);

  // Metadata (categories, sources, users, templates, agents) refreshes
  // freely. The layout/rows refresh is SEPARATE and destructive: it resets
  // local edits and the dirty flag, so it only runs when the server copy is
  // the truth (initial load, after save, after template apply, discard).
  // Category/agent saves must NOT reload rows, or they clobber the
  // appendBlock/removal they just made along with any unsaved arrangement.
  // Tracks the company this builder currently renders. A response resolving
  // after the admin switched companies must be discarded, or Save would post
  // the previous company's rows under the new homeCompany.
  const currentCompanyRef = React.useRef(props.homeCompany);
  currentCompanyRef.current = props.homeCompany;

  // isStillValid: standalone refreshes (after category/agent mutations)
  // only need the company check - their data is the current server state
  // either way. fetchAll passes its own isCurrent so a superseded load's
  // metadata cannot land after a newer load's.
  const fetchMetadata = async (isStillValid = null) => {
    const requestCompany = props.homeCompany;
    try {
      const [columnsRes, sourcesRes, usersRes, templatesRes, agentsRes] =
        await Promise.all([
          getRightworkAPI(API_get_logbook_columns, {
            company_id: props.homeCompany,
          }),
          getRightworkAPI(API_get_logbook_metric_sources, {
            company_id: props.homeCompany,
          }),
          getRightworkAPI(API_get_logbook_notify_users, {
            company_id: props.homeCompany,
          }),
          getRightworkAPI(API_get_logbook_templates, {
            company_id: props.homeCompany,
          }),
          getRightworkAPI(API_get_logbook_agents, {
            company_id: props.homeCompany,
          }),
        ]);
      if (
        isStillValid
          ? !isStillValid()
          : currentCompanyRef.current !== requestCompany
      ) {
        return false;
      }
      if (columnsRes.status === 200) {
        setColumnsData(columnsRes.data?.redux?.payload?.logbook_columns);
      }
      if (sourcesRes.status === 200) {
        setMetricSources(sourcesRes.data?.redux?.payload?.sources || []);
      }
      if (usersRes.status === 200) {
        setNotifyUsers(usersRes.data?.redux?.payload?.users || []);
      }
      if (templatesRes.status === 200) {
        const templates = templatesRes.data?.redux?.payload?.templates || [];
        setCategoryTemplates(templates);
        setSelectedTemplateId((current) => current || templates[0]?.id || null);
      }
      if (agentsRes.status === 200) {
        setAgents(agentsRes.data?.redux?.payload?.agents || []);
      }
      return true;
    } catch (error) {
      console.error("Failed to load layout builder metadata", error);
      // Standalone refreshes (after category/agent mutations) tolerate a
      // failure - existing metadata stays. fetchAll checks this flag so a
      // NAVIGATION load renders the failure state instead of the new
      // company's layout over the previous company's metadata.
      return false;
    }
  };

  // Orders overlapping same-company loads (e.g. a slow initial load
  // resolving after a post-save refetch): only the latest-started fetchAll
  // may write.
  const fetchSeqRef = React.useRef(0);

  // guardRevision: the post-save refetch passes the revision it saved at;
  // if edits land while the GET is in flight, the rows replacement (and
  // dirty reset) is skipped so those newer edits survive.
  const fetchAll = async (showLoading, guardRevision = null) => {
    const requestCompany = props.homeCompany;
    // Silent refetches pre-check the company BEFORE advancing the
    // sequence - a stale closure's refetch for company A bumping the
    // counter would invalidate company B's in-flight load while failing
    // its own check, deadlocking B on the spinner. Navigation loads define
    // the current company.
    if (showLoading) {
      currentCompanyRef.current = requestCompany;
    } else if (currentCompanyRef.current !== requestCompany) {
      return;
    }
    const requestSeq = ++fetchSeqRef.current;
    const isCurrent = () =>
      currentCompanyRef.current === requestCompany &&
      fetchSeqRef.current === requestSeq;
    setIsLoading(showLoading);
    if (showLoading) {
      // Navigation load: never leave the PREVIOUS company's rows OR
      // metadata mounted - if this request fails, Save would post that
      // stale layout under the new homeCompany, and stale categories,
      // sources, recipients, templates, and agents would dress the new
      // company's canvas.
      setRows([]);
      setDirty(false);
      setLoadFailed(false);
      setColumnsData(null);
      setMetricSources([]);
      setNotifyUsers([]);
      setCategoryTemplates([]);
      setAgents([]);
      setPlacement("end");
      setAddMenu(null);
      setSavedItemsOpen(false);
      setMoveMenu(null);
      setCategoryDrawerOpen(false);
      setSettingsBlock(null);
      setAgentDrawerOpen(false);
      setTemplateDrawerOpen(false);
    }
    try {
      const [layoutRes, metadataOk] = await Promise.all([
        getRightworkAPI(API_get_logbook_layout, {
          company_id: props.homeCompany,
        }),
        fetchMetadata(isCurrent),
      ]);
      // Full isCurrent, not just the company: two same-company loads can
      // overlap (initial load vs post-save refetch, discard vs refresh),
      // and the older response resolving LAST would overwrite the newer
      // rows and clear dirty.
      if (!isCurrent()) return;
      if (showLoading && !metadataOk) {
        // A half-loaded builder is worse than an error: missing categories
        // render as unknown blocks and saves get rejected.
        throw new Error("layout builder metadata failed");
      }
      if (guardRevision !== null && revisionRef.current !== guardRevision) {
        // Edits landed while this refetch was in flight: replacing rows now
        // would silently discard them and clear dirty. Metadata already
        // refreshed above; the local rows stay (still marked dirty).
        return;
      }
      if (layoutRes.status === 200) {
        const layout = layoutRes.data?.redux?.payload?.layout;
        setRows(layout?.layout_json || []);
        setIsLegacy(Boolean(layout?.is_legacy));
        setDirty(false);
        setLoadFailed(false);
      }
    } catch (error) {
      if (!isCurrent()) return;
      setLoadFailed(true);
      console.error("Failed to load layout builder data", error);
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  };

  React.useEffect(() => {
    fetchAll(true);
  }, [props.homeCompany]);

  React.useEffect(() => {
    const [position, uuid] = placement.split(":");
    if (position === "end") return;
    const target = rows.find((row) => row.uuid === uuid);
    if (!target) setPlacement("end");
    else if (position === "row" && !canShareRow(target))
      setPlacement(`after:${uuid}`);
  }, [rows, placement]);

  const categoriesByLineage = React.useMemo(() => {
    const map = {};
    (columnsData || []).forEach((column) => {
      (column.categories || []).forEach((category) => {
        map[category.lineage_id || category.uuid] = category;
      });
    });
    return map;
  }, [columnsData]);

  const placedLineages = new Set(
    rows.flatMap((row) =>
      row.blocks
        .filter((b) => b.type === "category")
        .map((b) => b.config?.lineage_id)
    )
  );
  const unplacedCategories = Object.entries(categoriesByLineage)
    .filter(([lineage]) => !placedLineages.has(lineage))
    .map(([, category]) => category);

  // The metric pickers only offer categories PLACED on the current canvas:
  // an unplaced category rejects new entries, so a metric sourcing it would
  // starve at no_data (and the backend rejects the save). Computed from
  // live local rows, so place-then-source works before saving.
  const availableSources = (metricSources || []).filter(
    (source) =>
      !source.key?.startsWith("category:") ||
      placedLineages.has(source.key.slice("category:".length))
  );

  // Agents persist on save in the drawer, but their block placement only
  // persists with "Save layout" - so an agent created (or removed from the
  // canvas) without a follow-up layout save would otherwise be unreachable:
  // no block to click means no way to edit, place, or archive it.
  const placedAgentUuids = new Set(
    rows.flatMap((row) =>
      row.blocks
        .filter((b) => b.type === "agent")
        .map((b) => b.config?.agent_uuid)
    )
  );
  const unplacedAgents = (agents || []).filter(
    (agent) => !placedAgentUuids.has(agent.uuid)
  );

  const leftColumnUuid = columnsData?.[0]?.uuid;

  const markDirty = () => setDirty(true);

  // Counts local layout mutations so saveLayout can tell whether edits
  // landed while its request was in flight.
  const revisionRef = React.useRef(0);

  const updateRows = (updater) => {
    setRows((prev) => updater(prev));
    revisionRef.current += 1;
    markDirty();
  };

  // ---------- block operations ----------

  const appendBlock = (block, destination = placement) => {
    const rowUuid = newUuid();
    updateRows((prev) => insertBlock(prev, block, destination, rowUuid));
  };

  const openAddMenu = (event, destination) => {
    setPlacement(destination);
    setAddMenu({ anchor: event.currentTarget, destination });
  };

  const updateBlockConfig = (blockUuid, config, extra = {}) => {
    updateRows((prev) =>
      prev.map((row) => ({
        ...row,
        blocks: row.blocks.map((block) =>
          block.uuid === blockUuid ? { ...block, ...extra, config } : block
        ),
      }))
    );
  };

  const saveBlockSettings = (config) => {
    if (settingsBlock.isNew) {
      const { isNew, destination, ...block } = settingsBlock;
      appendBlock({ ...block, config }, destination);
    } else {
      updateBlockConfig(settingsBlock.uuid, config);
    }
  };

  const removeBlock = (blockUuid) => {
    // Hiding a category also stops its writes (the entry endpoint rejects
    // unplaced lineages), so a metric card or table row still sourcing it
    // would sit at no_data forever. Take dependents off with it - after a
    // confirm, since they carry threshold config the admin set up.
    const target = rows
      .flatMap((row) => row.blocks)
      .find((block) => block.uuid === blockUuid);
    const lineage =
      target?.type === "category" ? target.config?.lineage_id : null;
    const sourceKey = lineage ? `category:${lineage}` : null;
    if (sourceKey) {
      const dependentCount = rows
        .flatMap((row) => row.blocks)
        .reduce((count, block) => {
          if (block.type === "metric" && block.config?.source === sourceKey) {
            return count + 1;
          }
          if (block.type === "table") {
            return (
              count +
              (block.config?.rows || []).filter(
                (tableRow) => tableRow?.source === sourceKey
              ).length
            );
          }
          return count;
        }, 0);
      if (dependentCount > 0) {
        const confirmed = window.confirm(
          `This category feeds ${dependentCount} metric${
            dependentCount === 1 ? "" : "s"
          } on the layout. Hiding it stops new entries, so ${
            dependentCount === 1 ? "that metric comes" : "those metrics come"
          } off too.`
        );
        if (!confirmed) return;
      }
    }
    updateRows((prev) =>
      prev
        .map((row) => ({
          ...row,
          blocks: row.blocks
            .filter((block) => block.uuid !== blockUuid)
            .filter(
              (block) =>
                !(
                  sourceKey &&
                  block.type === "metric" &&
                  block.config?.source === sourceKey
                )
            )
            .map((block) => {
              if (!sourceKey || block.type !== "table") return block;
              const keptRows = (block.config?.rows || []).filter(
                (tableRow) => tableRow?.source !== sourceKey
              );
              if (keptRows.length === (block.config?.rows || []).length) {
                return block;
              }
              return { ...block, config: { ...block.config, rows: keptRows } };
            })
            .filter(
              (block) =>
                !(
                  sourceKey &&
                  block.type === "table" &&
                  !(block.config?.rows || []).length
                )
            ),
        }))
        .filter((row) => row.blocks.length > 0)
    );
  };

  const toggleEmail = (block) => {
    updateBlockConfig(block.uuid, block.config, {
      include_in_email: !block.include_in_email,
    });
  };

  // ---------- persistence ----------

  const saveLayout = async () => {
    setIsSaving(true);
    const revisionAtSave = revisionRef.current;
    try {
      const res = await postRightworkAPI(API_post_logbook_layout, {
        company_id: props.homeCompany,
        // Empty rows are not meaningful to persist.
        layout_json: JSON.stringify(
          rows.filter((row) => row.blocks.length > 0)
        ),
      });
      if (res.status === 200) {
        toast.success("Layout saved", { autoClose: 3000 });
        if (revisionRef.current === revisionAtSave) {
          // Column sync may have re-versioned categories; refetch so blocks
          // keep pointing at fresh data. The guard carries the revision
          // through the GET too - edits made during the refetch survive.
          fetchAll(false, revisionAtSave);
        } else {
          // The admin kept editing while the save was in flight: reloading
          // the server snapshot would silently discard those newer edits
          // (and clear dirty). Keep the local rows - lineage references
          // survive column sync - and refresh metadata only.
          fetchMetadata();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.system?.message || "Request Failed", {
        autoClose: 3000,
      });
      console.error("Error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // ---------- categories (existing endpoints, unchanged) ----------

  const post_logbook_category = async () => {
    const isEdit = Boolean(categoryModalData.uuid);
    const requestCompany = props.homeCompany;
    const requestData = {
      company_id: props.homeCompany,
      logbook_category_uuid: categoryModalData.uuid,
      logbook_column_uuid: leftColumnUuid,
      name: categoryModalData.name.trim(),
      type: categoryModalData.type,
      notify_user_ids: JSON.stringify(categoryModalData.notify_user_ids || []),
      description: categoryModalData.description,
    };
    try {
      const res = await postRightworkAPI(
        API_post_logbook_category,
        requestData
      );
      if (res.status === 200) {
        if (currentCompanyRef.current !== requestCompany) return false;
        const category = res.data?.redux?.payload?.logbook_category;
        // Show the saved name immediately, even if the metadata refresh fails.
        if (category) {
          setColumnsData((previous) =>
            (previous || []).map((column, index) => ({
              ...column,
              categories: [
                ...(column.categories || []).filter(
                  (item) => item.uuid !== category.uuid
                ),
                ...((!isEdit && index === 0) ||
                column.categories?.some((item) => item.uuid === category.uuid)
                  ? [category]
                  : []),
              ],
            }))
          );
        }
        if (!isEdit && category?.lineage_id) {
          appendBlock({
            uuid: newUuid(),
            type: "category",
            include_in_email: true,
            config: { lineage_id: category.lineage_id },
          });
        }
        fetchMetadata();
        toast.success(
          isEdit
            ? "Category details saved"
            : "Category created. Save layout to publish its placement.",
          { autoClose: 4000 }
        );
        return true;
      }
      throw new Error("Could not save category. Please try again.");
    } catch (error) {
      // The drawer owns the error state so input is retained for a retry.
      throw new Error(
        error.response?.data?.system?.message ||
          error.message ||
          "Could not save category. Please try again."
      );
    }
  };

  const delete_logbook_category = async (category) => {
    const confirmed = window.confirm(
      `Delete the category "${category.name}"? Past entries are kept, but the category comes off the logbook.`
    );
    if (!confirmed) return;
    try {
      toast.loading("Deleting Category...", { autoClose: false });
      const res = await postRightworkAPI(API_delete_logbook_category, {
        company_id: props.homeCompany,
        logbook_category_uuid: category.uuid,
      });
      toast.dismiss();
      if (res.status === 200) {
        toast.success("Category Deleted", { autoClose: 3000 });
        // Drop everything still pointing at the deleted lineage: the
        // category block, metric cards sourcing it, and table rows with
        // that source (a table left with no rows goes too). Mirrors the
        // server-side strip delete_logbook_category applies to the STORED
        // layout, so local unsaved state stays in step.
        const lineage = category.lineage_id || category.uuid;
        const sourceKey = `category:${lineage}`;
        updateRows((prev) =>
          prev
            .map((row) => ({
              ...row,
              blocks: row.blocks
                .filter(
                  (block) =>
                    !(
                      block.type === "category" &&
                      block.config?.lineage_id === lineage
                    ) &&
                    !(
                      block.type === "metric" &&
                      block.config?.source === sourceKey
                    )
                )
                .map((block) => {
                  if (block.type !== "table") return block;
                  const keptRows = (block.config?.rows || []).filter(
                    (tableRow) => tableRow?.source !== sourceKey
                  );
                  if (keptRows.length === (block.config?.rows || []).length) {
                    return block;
                  }
                  return {
                    ...block,
                    config: { ...block.config, rows: keptRows },
                  };
                })
                .filter(
                  (block) =>
                    !(
                      block.type === "table" &&
                      !(block.config?.rows || []).length
                    )
                ),
            }))
            .filter((row) => row.blocks.length > 0)
        );
        setCategoryDrawerOpen(false);
        setCategoryModalData(DEFAULT_CATEGORY_MODAL_DATA);
        fetchMetadata();
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.system?.message || "Request Failed", {
        autoClose: 3000,
      });
    }
  };

  const applyTemplate = async () => {
    if (!selectedTemplateId) return;
    const confirmed = window.confirm(
      "Use this layout? Your current arrangement is replaced; categories and " +
        "entries are always kept, and anything the template does not mention " +
        "stays available under Saved items in the add menu."
    );
    if (!confirmed) return;
    setIsApplyingTemplate(true);
    try {
      const res = await postRightworkAPI(API_post_apply_logbook_template, {
        company_id: props.homeCompany,
        template_id: selectedTemplateId,
      });
      if (res.status === 200) {
        const payload = res.data?.redux?.payload;
        const created = payload?.created_count ?? 0;
        const agents = payload?.created_agent_names || [];
        const parts = ["Template applied"];
        if (created > 0) {
          parts.push(
            `${created} new ${created === 1 ? "category" : "categories"}`
          );
        }
        if (agents.length > 0) {
          parts.push(`agent "${agents[0]}" created`);
        }
        toast.success(parts.join(" · "), { autoClose: 4000 });
        setTemplateDrawerOpen(false);
        // The applied layout is now the saved layout; refetch everything.
        fetchAll(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.system?.message || "Request Failed", {
        autoClose: 3000,
      });
    } finally {
      setIsApplyingTemplate(false);
    }
  };

  // ---------- palette ----------

  const paletteAdd = (type) => {
    if (type === "category") {
      setCategoryModalData(DEFAULT_CATEGORY_MODAL_DATA);
      setCategoryModalEdit(false);
      setCategoryDrawerOpen(true);
      return;
    }
    if (type === "metric") {
      const block = {
        uuid: newUuid(),
        type: "metric",
        include_in_email: true,
        config: { source: "net_sales", direction: "flag_below" },
      };
      setSettingsBlock({ ...block, isNew: true, destination: placement });
      return;
    }
    if (type === "table") {
      const block = {
        uuid: newUuid(),
        type: "table",
        include_in_email: true,
        config: {
          label: "Store metrics",
          rows: [],
          columns: ["value", "vs_last_week", "status"],
        },
      };
      setSettingsBlock({ ...block, isNew: true, destination: placement });
      return;
    }
    if (type === "section") {
      const block = {
        uuid: newUuid(),
        type: "section",
        include_in_email: true,
        config: { label: "" },
      };
      setSettingsBlock({ ...block, isNew: true, destination: placement });
      return;
    }
    if (type === "agent") {
      setEditingAgent(null);
      setAgentDrawerOpen(true);
    }
  };

  const openBlockSettings = (block) => {
    if (block.type === "category") {
      const category = categoriesByLineage[block.config?.lineage_id];
      if (category) {
        setCategoryModalData(category);
        setCategoryModalEdit(true);
        setCategoryDrawerOpen(true);
      }
      return;
    }
    if (block.type === "agent") {
      const agent = agents.find((a) => a.uuid === block.config?.agent_uuid);
      setEditingAgent(agent || null);
      setAgentDrawerOpen(true);
      return;
    }
    setSettingsBlock(block);
  };

  const handleAgentSaved = (agent, wasEdit, requestCompany) => {
    if (!agent) return;
    // Same mid-flight company-switch guard as the category save: never
    // append or relabel blocks in another company's rows.
    if (requestCompany && currentCompanyRef.current !== requestCompany) return;
    if (wasEdit) {
      // Keep block labels in step with the agent's name.
      updateRows((prev) =>
        prev.map((row) => ({
          ...row,
          blocks: row.blocks.map((block) =>
            block.type === "agent" && block.config?.agent_uuid === agent.uuid
              ? { ...block, config: { ...block.config, label: agent.name } }
              : block
          ),
        }))
      );
    } else {
      appendBlock({
        uuid: newUuid(),
        type: "agent",
        include_in_email: true,
        config: { agent_uuid: agent.uuid, label: agent.name },
      });
    }
    fetchMetadata();
  };

  const handleAgentArchived = (agent) => {
    updateRows((prev) =>
      prev
        .map((row) => ({
          ...row,
          blocks: row.blocks.filter(
            (block) =>
              !(
                block.type === "agent" &&
                block.config?.agent_uuid === agent.uuid
              )
          ),
        }))
        .filter((row) => row.blocks.length > 0)
    );
    fetchMetadata();
  };

  const blockLabel = (block) => {
    if (block.type === "category") {
      return (
        categoriesByLineage[block.config?.lineage_id]?.name ||
        "Missing category"
      );
    }
    if (block.type === "metric") {
      return (
        block.config?.label ||
        metricSources.find((s) => s.key === block.config?.source)?.label ||
        "Metric"
      );
    }
    if (block.type === "table") {
      return block.config?.label || "Metric table";
    }
    if (block.type === "section") {
      return block.config?.label || "Section";
    }
    if (block.type === "agent") {
      return (
        agents.find((a) => a.uuid === block.config?.agent_uuid)?.name ||
        block.config?.label ||
        "AI Agent"
      );
    }
    return block.type;
  };

  const actionRow = rows.find((row) => row.uuid === moveMenu?.rowUuid);
  const actionBlockIndex =
    actionRow?.blocks.findIndex(
      (block) => block.uuid === moveMenu?.block.uuid
    ) ?? -1;

  if (isLoading) {
    return (
      <div className="w-full h-64 relative">
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="w-full h-64 flex flex-col items-center justify-center">
        <p className="text-sm text-slate-500">
          The layout could not be loaded for this company.
        </p>
        <button
          className="mt-3 text-xs font-semibold text-violet-700 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:border-violet-400"
          onClick={() => fetchAll(true)}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Canvas */}
      <div className="flex-1 min-w-0 w-full">
        <div className="flex flex-wrap items-center gap-3 mb-6 sticky top-0 z-10 bg-white pt-3 pb-5 border-b border-slate-200">
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900">
              Company layout
            </h2>
            <p className="text-xs text-slate-500">
              Shared by every store · open a card’s menu to edit or arrange.
              {isLegacy && " · not saved yet, showing your current logbook"}
            </p>
          </div>
          <button
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:border-violet-400 hover:text-violet-700 focus-visible:ring-2 focus-visible:ring-violet-500"
            onClick={() => setTemplateDrawerOpen(true)}
          >
            <ViewQuiltOutlinedIcon style={{ fontSize: 16 }} /> Browse templates
          </button>
          {dirty && !isSaving && (
            <button
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-400"
              onClick={() => {
                if (
                  window.confirm(
                    "Discard unsaved layout changes? Category and agent details already saved will be kept."
                  )
                )
                  fetchAll(false);
              }}
            >
              Discard changes
            </button>
          )}
          <button
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              dirty && !isSaving
                ? "bg-violet-700 text-white cursor-pointer hover:bg-violet-800"
                : "bg-slate-100 text-slate-400"
            }`}
            disabled={!dirty || isSaving}
            onClick={saveLayout}
          >
            {isSaving ? "Saving..." : "Save layout"}
          </button>
        </div>

        {rows.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <h3 className="font-semibold text-slate-800">
              Start with a log category
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Create a place for shift notes, then add metrics or headings as
              needed.
            </p>
            <button
              className="mt-4 rounded-lg bg-violet-700 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-800 focus-visible:ring-2 focus-visible:ring-violet-500"
              onClick={() => paletteAdd("category")}
            >
              Create category
            </button>
          </div>
        )}
        <div className="flex flex-col gap-3">
          {rows.map((row) => (
            <section
              key={row.uuid}
              aria-label={`Row ${rows.indexOf(row) + 1}`}
              className="grid grid-cols-12 gap-3 items-start"
            >
              {row.blocks.map((block) => (
                <div
                  key={block.uuid}
                  className={`min-w-0 ${SPAN_BY_ROW_COUNT[row.blocks.length] || "col-span-12"}`}
                >
                  <ConfigurationBlockPreview
                    block={block}
                    label={blockLabel(block)}
                    category={categoriesByLineage[block.config?.lineage_id]}
                    sources={metricSources}
                    onEdit={() => openBlockSettings(block)}
                    actions={
                      <button
                        className={ACTION_CLASS}
                        aria-label={`Actions for ${blockLabel(block)}`}
                        aria-haspopup="menu"
                        onClick={(event) =>
                          setMoveMenu({
                            anchor: event.currentTarget,
                            block,
                            rowUuid: row.uuid,
                          })
                        }
                      >
                        <MoreHorizIcon style={{ fontSize: 20 }} />
                      </button>
                    }
                  />
                </div>
              ))}
            </section>
          ))}
        </div>

        <button
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-3 border border-dashed border-slate-300 rounded-xl text-slate-600 hover:border-violet-400 hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          aria-haspopup="menu"
          onClick={(event) => openAddMenu(event, "end")}
        >
          <AddIcon style={{ fontSize: 16 }} />
          <span className="text-sm font-medium">Add to logbook</span>
        </button>
      </div>

      <Menu
        {...LAYOUT_MENU_PROPS}
        anchorEl={addMenu?.anchor}
        open={Boolean(addMenu)}
        onClose={() => setAddMenu(null)}
      >
        {BLOCK_TYPES.filter(
          ([type]) =>
            type !== "section" || !addMenu?.destination.startsWith("row:")
        ).map(([type, label, description]) => (
          <MenuItem
            key={type}
            aria-label={label}
            onClick={() => {
              setAddMenu(null);
              paletteAdd(type);
            }}
          >
            <span className="flex flex-col gap-0.5">
              <span className="font-semibold">{label}</span>
              <span className="text-xs text-slate-500">{description}</span>
            </span>
          </MenuItem>
        ))}
        {(unplacedCategories.length > 0 || unplacedAgents.length > 0) && [
          <Divider key="saved-divider" />,
          <MenuItem
            key="saved-items"
            onClick={() => {
              setAddMenu(null);
              setSavedItemsOpen(true);
            }}
          >
            Saved items
          </MenuItem>,
        ]}
      </Menu>
      <Menu
        {...LAYOUT_MENU_PROPS}
        anchorEl={moveMenu?.anchor}
        open={Boolean(moveMenu)}
        onClose={() => setMoveMenu(null)}
      >
        <MenuItem
          onClick={() => {
            openBlockSettings(moveMenu.block);
            setMoveMenu(null);
          }}
        >
          Edit settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggleEmail(moveMenu.block);
            setMoveMenu(null);
          }}
        >
          {moveMenu?.block.include_in_email
            ? "Remove from daily email"
            : "Include in daily email"}
        </MenuItem>
        <Divider />
        {[-1, 1].map((offset) => (
          <MenuItem
            key={offset}
            aria-label={`Move row ${rows.findIndex((row) => row.uuid === moveMenu?.rowUuid) + 1} ${offset < 0 ? "up" : "down"}`}
            disabled={
              !moveMenu ||
              rows.findIndex((row) => row.uuid === moveMenu.rowUuid) + offset <
                0 ||
              rows.findIndex((row) => row.uuid === moveMenu.rowUuid) + offset >=
                rows.length
            }
            onClick={() => {
              updateRows((prev) => moveRow(prev, moveMenu.rowUuid, offset));
              setMoveMenu(null);
            }}
          >
            {moveMenu &&
            rows.find((row) => row.uuid === moveMenu.rowUuid)?.blocks.length > 1
              ? "Move row"
              : "Move"}{" "}
            {offset < 0 ? "up" : "down"}
          </MenuItem>
        ))}
        {actionRow?.blocks.length > 1 &&
          [-1, 1].map((offset) => (
            <MenuItem
              key={`horizontal-${offset}`}
              disabled={
                actionBlockIndex + offset < 0 ||
                actionBlockIndex + offset >= actionRow.blocks.length
              }
              onClick={() => {
                updateRows((prev) =>
                  moveBlockWithinRow(
                    prev,
                    moveMenu.rowUuid,
                    moveMenu.block.uuid,
                    offset
                  )
                );
                setMoveMenu(null);
              }}
            >
              Move {offset < 0 ? "left" : "right"}
            </MenuItem>
          ))}
        {actionRow?.blocks.length > 1 &&
          [-1, 1].map((offset) => (
            <MenuItem
              key={`new-row-${offset}`}
              onClick={() => {
                const rowUuid = newUuid();
                updateRows((prev) =>
                  moveBlockToNewRow(
                    prev,
                    moveMenu.rowUuid,
                    moveMenu.block.uuid,
                    offset,
                    rowUuid
                  )
                );
                setMoveMenu(null);
              }}
            >
              Move to new row {offset < 0 ? "above" : "below"}
            </MenuItem>
          ))}
        <Divider />
        {[
          ["before", "Add above row"],
          ["after", "Add below row"],
          ...(moveMenu &&
          canShareRow(rows.find((row) => row.uuid === moveMenu.rowUuid))
            ? [["row", "Add beside items in row"]]
            : []),
        ].map(([position, text]) => (
          <MenuItem
            key={position}
            aria-label={`${text} ${rows.findIndex((row) => row.uuid === moveMenu?.rowUuid) + 1}`}
            onClick={() => {
              openAddMenu(
                { currentTarget: moveMenu.anchor },
                `${position}:${moveMenu.rowUuid}`
              );
              setMoveMenu(null);
            }}
          >
            {position === "before"
              ? "Add above"
              : position === "after"
                ? "Add below"
                : "Add beside"}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            removeBlock(moveMenu.block.uuid);
            setMoveMenu(null);
          }}
        >
          <span className="text-rose-600">Remove from layout</span>
        </MenuItem>
      </Menu>

      <SavedLogbookItemsDrawer
        open={savedItemsOpen}
        onClose={() => setSavedItemsOpen(false)}
        categories={unplacedCategories}
        agents={unplacedAgents}
        onRestoreCategory={(category) => {
          appendBlock({
            uuid: newUuid(),
            type: "category",
            include_in_email: true,
            config: { lineage_id: category.lineage_id || category.uuid },
          });
          setSavedItemsOpen(false);
        }}
        onDeleteCategory={delete_logbook_category}
        onRestoreAgent={(agent) => {
          appendBlock({
            uuid: newUuid(),
            type: "agent",
            include_in_email: true,
            config: { agent_uuid: agent.uuid, label: agent.name },
          });
          setSavedItemsOpen(false);
        }}
        onEditAgent={(agent) => {
          setSavedItemsOpen(false);
          setEditingAgent(agent);
          setAgentDrawerOpen(true);
        }}
      />

      {/* Drawers */}
      <LogbookCategoryDrawer
        key={props.homeCompany}
        open={categoryDrawerOpen}
        onClose={() => {
          setCategoryDrawerOpen(false);
          setCategoryModalData(DEFAULT_CATEGORY_MODAL_DATA);
        }}
        modalData={categoryModalData}
        setModalData={setCategoryModalData}
        modalEdit={categoryModalEdit}
        chatUsers={notifyUsers}
        post_logbook_category={post_logbook_category}
      />
      <MetricBlockDrawer
        open={Boolean(settingsBlock && settingsBlock.type === "metric")}
        onClose={() => setSettingsBlock(null)}
        block={settingsBlock}
        sources={availableSources}
        // Per-store targets are company-scoped: storeNames spans every
        // company the admin belongs to, but this layout only evaluates for
        // stores in homeCompany.
        storeNames={(props.storeNames || []).filter(
          (store) => store.company === props.homeCompany
        )}
        notifyOptions={notifyUsers}
        onSave={saveBlockSettings}
      />
      <TableBlockDrawer
        open={Boolean(settingsBlock && settingsBlock.type === "table")}
        onClose={() => {
          const closing = settingsBlock;
          setSettingsBlock(null);
          // A table with no metric rows renders as bare headers (and the
          // backend now rejects it on save), so closing the drawer without
          // adding any rows removes the draft block.
          if (closing?.type === "table" && !closing.isNew) {
            updateRows((prev) =>
              prev
                .map((row) => ({
                  ...row,
                  blocks: row.blocks.filter(
                    (block) =>
                      !(
                        block.uuid === closing.uuid &&
                        block.type === "table" &&
                        !(block.config?.rows || []).length
                      )
                  ),
                }))
                .filter((row) => row.blocks.length > 0)
            );
          }
        }}
        block={settingsBlock}
        sources={availableSources}
        notifyOptions={notifyUsers}
        // Decide from the SUBMITTED config: saving with every metric row
        // deleted removes the table (the backend rejects rowless tables),
        // without relying on the close handler seeing the updated state.
        onSave={(config) =>
          (config.rows || []).length
            ? saveBlockSettings(config)
            : !settingsBlock.isNew && removeBlock(settingsBlock.uuid)
        }
      />
      <SectionBlockDrawer
        open={Boolean(settingsBlock && settingsBlock.type === "section")}
        onClose={() => setSettingsBlock(null)}
        block={settingsBlock}
        onSave={saveBlockSettings}
      />
      <AgentBlockDrawer
        open={agentDrawerOpen}
        onClose={() => {
          setAgentDrawerOpen(false);
          setEditingAgent(null);
        }}
        agent={editingAgent}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        onSaved={handleAgentSaved}
        onArchived={handleAgentArchived}
      />
      <LogbookTemplateDrawer
        open={templateDrawerOpen}
        onClose={() => setTemplateDrawerOpen(false)}
        templates={categoryTemplates}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        isApplyingTemplate={isApplyingTemplate}
        applyTemplate={applyTemplate}
      />
    </div>
  );
}

export default LogbookLayoutBuilder;
